import { Flex, NumberField, Typography } from '@iheartjane/reefer';

import type {
  ConditionRangeItem,
  ConditionRangeValue,
} from './conditions.types';
import { toBound } from './util/toBound';

/** Field names double as DOM ids, so they have to be label-unique */
const toFieldName = (
  prefix: string,
  label: string,
  bound: 'maximum' | 'minimum'
): string => `${prefix}-${label.toLowerCase().replace(/\s+/g, '-')}-${bound}`;

export interface RangeConditionsProps {
  /** Decimal places the inputs accept, ex 2 for dollars and cents */
  allowedDecimalPlaces?: number;

  disabled?: boolean;

  /** Unit rendered to the right of the inputs, ex "%" */
  endUnit?: string;
  /**
   * Validation message to render beneath the inputs. The caller owns the rule —
   * `validateRange` computes the message this expects.
   */
  error?: string;

  /** Namespaces the field names/ids, unique per group rendered on the page */
  fieldNamePrefix: string;

  /** The range's label and the bounds a selection must fall within */
  item: ConditionRangeItem;

  /** Fired with the whole range, so an untouched bound carries over unchanged */
  onChange(value: ConditionRangeValue): void;

  /** Unit rendered to the left of the inputs, ex "$" */
  startUnit?: string;

  /** Legal increment for the inputs, ex 0.01 for dollars and cents */
  step?: number;

  /**
   * The selected range, open on both ends until something is typed.
   *
   * NB: Reefer's `NumberField` ignores an `undefined` `value`, so clearing a
   * bound from out here will not empty the input. Callers that need a hard
   * reset have to remount.
   */
  value: ConditionRangeValue;
}

/**
 * A numeric min/max pair for one range condition, ex a price floor and ceiling.
 *
 * Presentational: the bounds in `item` describe what is allowed but are not
 * enforced here, and the validation message is passed in rather than derived.
 */
export const RangeConditions = ({
  allowedDecimalPlaces,
  disabled,
  endUnit,
  error,
  fieldNamePrefix,
  item,
  onChange,
  startUnit,
  step,
  value,
}: RangeConditionsProps) => {
  const sharedFieldProps = {
    allowedDecimalPlaces,
    disabled,
    disableOnWheel: true,
    endUnit,
    startUnit,
    step,
    width: '100%',
  };

  return (
    <Flex flexDirection="column" gap={8}>
      <Flex alignItems="flex-start" gap={24}>
        <NumberField
          {...sharedFieldProps}
          label={`${item.label} minimum`}
          name={toFieldName(fieldNamePrefix, item.label, 'minimum')}
          placeholder={String(item.min ?? '')}
          value={value.min}
          onChange={(min) => onChange({ ...value, min: toBound(min) })}
        />
        <NumberField
          {...sharedFieldProps}
          label={`${item.label} maximum`}
          name={toFieldName(fieldNamePrefix, item.label, 'maximum')}
          placeholder={String(item.max ?? '')}
          value={value.max}
          onChange={(max) => onChange({ ...value, max: toBound(max) })}
        />
      </Flex>
      {error && (
        <Typography color="error" aria-live="polite">
          {error}
        </Typography>
      )}
    </Flex>
  );
};
